import { en } from './';

export const results = {
  title: 'Your Results',
  subtitle: 'Comprehensive behavioral analysis',
  cognitiveAssessmentCTA: 'Complete Your Profile',
  cognitiveAssessmentDescription: 'Take the Cognitive Assessment to get your complete profile',
  downloadPDF: 'Download PDF',
  header: {
    assessmentComplete: 'Assessment Complete',
    yourResults: 'Your Results'
  },
  navigation: {
    backToHome: 'Back to Home'
  },
  pdfDownload: {
    success: {
      title: 'PDF Downloaded',
      description: 'Your behavioral assessment report has been downloaded successfully.'
    },
    error: {
      title: 'PDF Export Failed',
      description: 'Unable to generate PDF. Please try again.'
    }
  },
  cinematic: {
    home: 'Home',
    discoverYourStory: 'Discover Your Story',
    youAre: 'You Are'
  },
  aiInsights: {
    aiPoweredAnalysis: 'AI-Powered Analysis',
    yourSuperpowers: 'Your Key Strengths',
    revealed: 'Revealed',
    advancedAnalysis: 'Advanced AI analysis of your personality patterns reveals your unique strengths and growth opportunities',
    keySuperpowers: 'Your Key Strengths',
    deepInsights: 'Deep Insights',
    title: 'Your Work Style Strengths',
    badge: 'AI-Powered Analysis',
    subtitle: 'Celebrating your unique strengths and areas for mindful growth',
    regenerate: 'New Analysis',
    regenerating: 'Regenerating...',
    backupBadge: 'Using Backup Analysis',
    keyInsights: 'Key Insights',
    detailedAnalysis: 'Detailed Analysis'
  },
  objectivity: {
    thinkingStyle: 'Thinking Style',
    decisionMakingStyle: 'Decision-Making Style',
    howYouMakeDecisions: 'How You Make Decisions',
    uniqueApproach: 'Your unique approach to analyzing information and making choices',
    highlyAnalytical: 'Highly Analytical',
    dataDriven: 'Data-Driven',
    balancedApproach: 'Balanced Approach',
    peopleFocused: 'People-Focused',
    highlyIntuitive: 'Highly Intuitive',
    intuitiveLeader: 'Intuitive Leader',
    intuitive: 'Intuitive',
    balanced: 'Balanced',
    analytical: 'Analytical',
    descriptions: {
      veryHigh: 'You excel at making purely logical, fact-based decisions with exceptional analytical clarity.',
      high: 'You combine strong analytical thinking with practical decision-making, favoring data while staying flexible.',
      moderate: 'You masterfully balance logical analysis with emotional intelligence, considering both facts and people.',
      low: 'You prioritize relationships and human factors while incorporating relevant data into your decisions.',
      veryLow: 'You lead with intuition and emotional intelligence, creating people-centered solutions that build strong relationships.'
    }
  },
  personalityStory: {
    yourPersonalityStory: 'Your Personality Story',
    selfVsWork: 'Behavior',
    analysis: 'Analysis',
    theDataBehind: 'The Data Behind',
    yourUniqueness: 'Your Uniqueness',
    compareDescription: 'Compare how you see yourself with your work expectations',
    basedOnWords: 'Here\'s what makes you extraordinary',
    selfAssessment: 'Self Assessment',
    workBehavior: 'Work Behavior',
    naturalSelf: 'Natural Self',
    workPersona: 'Work Persona',
    yourBehavioralProfile: 'Your Behavioral Profile',
    basedOnSelectedWords: 'Your behavioral data'
  },
  behavioral: {
    basedOnWords: 'Your behavioral analysis',
    noDataAvailable: 'No data available for this section.',
    warningNoResults: 'Warning: No behavioral results found for this section.',
    warningDetails: 'This can happen if data did not load correctly or the assessment was incomplete.',
    noChartData: 'No chart data available.'
  },
  companyResults: {
    submittedTitle: 'Assessment Submitted Successfully',
    submittedSubtitle: 'Your results have been securely sent to the company. Here\'s a summary of your performance.',
    selfBehavior: 'Self Behavior',
    workBehavior: 'Work Behavior',
    profile: 'Profile',
    drive: 'Drive',
    connection: 'Connection',
    task: 'Task',
    order: 'Order',
    aiInsightsTitle: 'AI Personality Insights',
    refresh: 'Refresh',
    keyStrengths: 'Key Strengths',
    growthAreas: 'Growth Areas',
    recommendations: 'Recommendations',
    insightsError: 'AI insights could not be generated. Please try refreshing.',
    whatsNext: 'What\'s Next?',
    whatsNextDesc: 'Your results have been saved and shared with the assessment administrator. They will review your profile and may reach out with next steps.',
    returnToHome: 'Return to Home',
    workBehaviorProfile: 'Work Behavior Profile',
    yourProfile: 'Your Profile',
    submittedSubtitleBehavioral: 'Your results have been securely sent to the company. Here\'s a summary of your behavioral profile.',
    resultsSentSimple: 'Your results have been securely sent to the company.',
    thankYouMessage: 'Thank you for your time and effort in completing this assessment.',
    assessmentComplete: 'Assessment Complete',
    cognitiveResultsSubtitle: 'Here are your cognitive assessment results',
    overallCognitiveScore: 'Overall Cognitive Score',
    percentile: 'percentile',
    scoredHigherThan: 'You scored higher than {{percentile}}% of test-takers',
    scoreBreakdown: 'Score Breakdown',
    verbalReasoning: 'Verbal Reasoning',
    verbalDescription: 'Language comprehension and verbal logic',
    numericalReasoning: 'Numerical Reasoning',
    numericalDescription: 'Mathematical and quantitative analysis',
    abstractReasoning: 'Abstract Reasoning',
    abstractDescription: 'Pattern recognition and logical thinking',
    exceptional: 'Exceptional',
    aboveAverage: 'Above Average',
    average: 'Average',
    belowAverage: 'Below Average',
    needsDevelopment: 'Needs Development'
  }
};

export const factors = {
  // DCTOS Framework names
  drive: 'Drive',
  connection: 'Connection',
  task: 'Task',
  order: 'Order',
  style: 'Style',
  // Legacy names (mapped to DCTOS)
  dominance: 'Drive',
  extraversion: 'Connection',
  patience: 'Task',
  formality: 'Order',
  objectivity: 'Style',
  behavioralProfile: 'Behavioral Profile',
  descriptions: {
    // DCTOS descriptions
    drive: 'How you approach problems and challenges',
    connection: 'How you interact and influence others',
    task: 'How you respond to pace and change',
    order: 'How you respond to rules and procedures',
    style: 'How you process information and make decisions',
    // Legacy descriptions
    dominance: 'How you approach problems and challenges',
    extraversion: 'How you interact and influence others',
    patience: 'How you respond to pace and change',
    formality: 'How you respond to rules and procedures',
    objectivity: 'How you process information and make decisions'
  },
  category: {
    veryLow: 'Very Low',
    low: 'Low',
    moderate: 'Moderate',
    high: 'High',
    veryHigh: 'Very High'
  }
};

export const insights = {
  dominance: {
    veryHigh: 'Natural leader who drives results through decisive action',
    high: 'Takes initiative and comfortable making important decisions',
    moderate: 'Balanced approach to leadership and collaboration',
    low: 'Prefers collaborative decision-making and team consensus',
    veryLow: 'Strong preference for team-based solutions'
  },
  extraversion: {
    veryHigh: 'Highly social and energized by people interactions',
    high: 'Outgoing and comfortable in social situations',
    moderate: 'Balanced social interaction preferences',
    low: 'Values meaningful, one-on-one interactions',
    veryLow: 'Prefers deep, focused work environments'
  },
  patience: {
    veryHigh: 'Extremely stable and prefers consistent environments',
    high: 'Patient and deliberate in approach',
    moderate: 'Adaptable to different work paces',
    low: 'Prefers dynamic, fast-paced environments',
    veryLow: 'Thrives on change and quick decision-making'
  },
  formality: {
    veryHigh: 'Highly systematic and detail-oriented approach',
    high: 'Values structure and follows established procedures',
    moderate: 'Balances structure with flexibility',
    low: 'Adaptive and innovative thinking',
    veryLow: 'Highly flexible and creative approach'
  },
  objectivity: {
    veryHigh: 'Makes decisions based purely on facts and logical analysis',
    high: 'Generally objective with good analytical thinking',
    moderate: 'Balances logical analysis with intuitive insights',
    low: 'Considers emotions and relationships along with facts',
    veryLow: 'Highly intuitive and values emotional intelligence'
  }
};

export const tabs = {
  overview: 'Overview',
  detailed: 'Detailed Analysis',
  development: 'Development',
  career: 'Career Paths',
  words: 'Words'
};

export const professional = {
  style: {
    title: 'Professional Style',
    subtitle: 'Your natural approach to work and collaboration'
  }
};

export const objectivity = {
  model: {
    calculated: 'Calculated',
    direct: 'Direct',
    reverse: 'Reverse'
  },
  description: {
    calculated: 'Based on Order minus Connection',
    direct: 'Based on selected objectivity words',
    reverse: 'Start at 100%, deduct for subjective words'
  }
};
